import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type ProviderConfigError, parsedErrorOrNull } from './errors';
import { useForgetRequest } from './forgetRequest';
import { mapLocationConfig, toSaveRequest } from './mappers';
import { providerConfigQueryKey } from './queryKeys';
import { saveProviderConfigRequest } from './secretRequests';
import type { LocationProviderConfig, SaveProviderConfigInput } from './types';

export interface UseSaveProviderConfigResult {
    /** Rejects with the raw error; `error` holds it parsed. */
    save: (input: SaveProviderConfigInput) => Promise<LocationProviderConfig>;
    isSaving: boolean;
    error: ProviderConfigError | null;
    /** Drops the request, typed secrets included, from the mutation cache. */
    reset: () => void;
}

export function useSaveProviderConfig(
    locationId: number,
    providerName: string
): UseSaveProviderConfigResult {
    const queryClient = useQueryClient();
    const { mutationKey, forget } = useForgetRequest('save');
    const mutation = useMutation({
        mutationKey,
        mutationFn: saveProviderConfigRequest,
        // The variables, and error.response.config.data on a failure, carry typed secrets.
        // Without this the mutation keeps them in the cache for 5 minutes after unmount;
        // while mounted they live until reset.
        gcTime: 0,
        // Every card of the site reads the same query, and an inactive one must not
        // show its pre-save rows when it mounts again.
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: providerConfigQueryKey(locationId),
                refetchType: 'all',
            }),
    });
    const { mutateAsync } = mutation;

    const save = useCallback(
        async (input: SaveProviderConfigInput) => {
            const response = await mutateAsync({
                locationId,
                providerName,
                requestBody: toSaveRequest(input),
            });
            return mapLocationConfig(response.data);
        },
        [mutateAsync, locationId, providerName]
    );
    const error = useMemo(() => parsedErrorOrNull(mutation.error), [mutation.error]);
    const { reset: resetResult } = mutation;
    const reset = useCallback(() => {
        resetResult();
        forget();
    }, [resetResult, forget]);

    return { save, isSaving: mutation.isPending, error, reset };
}
